import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import type { ROASResult } from "../api";

const ROLLING_WINDOW = 7;
const ROLLING_THRESHOLD = 30; // show rolling overlay when range covers more than 30 days

interface SeriesPoint {
    day: string;
    roas: number;
    rolling?: number | null;
}

function withRolling(series: { day: string; roas: number }[]): SeriesPoint[] {
    if (series.length <= ROLLING_THRESHOLD) {
        return series.map((p) => ({ day: p.day, roas: p.roas }));
    }
    return series.map((p, i) => {
        const start = Math.max(0, i - ROLLING_WINDOW + 1);
        const window = series.slice(start, i + 1);
        const avg = window.reduce((s, w) => s + w.roas, 0) / window.length;
        return { day: p.day, roas: p.roas, rolling: avg };
    });
}

export function RoasChart({ data }: { data: ROASResult }) {
    const series = withRolling(data.series);
    const showRolling = data.series.length > ROLLING_THRESHOLD;
    return (
        <div className="bg-okiru-panel border border-okiru-muted/15 rounded-xl p-5 h-full min-h-0 flex flex-col">
            <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-sm uppercase tracking-wide text-okiru-muted">Blended ROAS</h3>
                <div className="text-xs text-okiru-muted">{showRolling ? "daily + 7-day avg" : "daily"}</div>
            </div>
            <ResponsiveContainer width="100%" height="100%" className="flex-1 min-h-0">
                <LineChart data={series} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a241e" />
                    <XAxis dataKey="day" stroke="#8a8175" fontSize={11} tickMargin={8} />
                    <YAxis
                        stroke="#8a8175"
                        fontSize={11}
                        tickFormatter={(v: number) => `${v.toFixed(1)}×`}
                    />
                    <Tooltip
                        contentStyle={{
                            background: "#1a1612",
                            border: "1px solid #3a322a",
                            borderRadius: 8,
                            color: "#f0e9df",
                        }}
                        formatter={(v: number, name: string) => [`${v.toFixed(2)}×`, name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: "#8a8175" }} />
                    <ReferenceLine y={1} stroke="#5c4d3e" strokeDasharray="2 4" />
                    <Line
                        type="monotone"
                        dataKey="roas"
                        name="Daily"
                        stroke="#c08552"
                        strokeWidth={2}
                        dot={false}
                    />
                    {showRolling && (
                        <Line
                            type="monotone"
                            dataKey="rolling"
                            name="7-day avg"
                            stroke="#f0e9df"
                            strokeWidth={1.5}
                            strokeDasharray="4 3"
                            dot={false}
                        />
                    )}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}
